import { Typography, Body } from '@optimus-web/core';
import { WithServiceId } from '@mews-commander/types';
import { LocalizedDayNameFromUtc, LocalizedUtcTimeUnit, SpacedComma, SpacedDash } from '@mews-commander/ui';
import { TimeUnitUtils, useTimezone } from '@mews/framework';
import { ServiceType, TimeUnitPeriod } from '@mews-commander/data-access';
import { getService } from '@mews-commander/legacy-entities/src/ducks/services';
import { useCommanderSelector } from '@mews-commander/utils';
import { AvailabilityBlockTimeUnitCount } from '../AvailabilityBlockTimeUnitCount';
import { useAvailabilityBlockInfo } from '../../utils/useAvailabilityBlockInfo';

interface DateRangePropsLocal extends WithServiceId {
    availabilityBlockId: string;
}

export const DateRange = ({ serviceId, availabilityBlockId }: DateRangePropsLocal) => {
    const service = useCommanderSelector((state) => getService(state, { id: serviceId }));
    const timezone = useTimezone();

    const { availabilityBlock } = useAvailabilityBlockInfo(availabilityBlockId);

    if (!availabilityBlock || service?.data?.discriminator !== ServiceType.Bookable) {
        return null;
    }

    const { timeUnitPeriod, endOffset } = service.data.value;

    const { endUtc, startUtc } = availabilityBlock;

    const timeUnitEndUtc = TimeUnitUtils.getNextOverlappingTimeUnitUtc({
        utc: endUtc,
        timeUnit: timeUnitPeriod,
        endOffset,
        timezone,
    });

    return (
        <div>
            <Typography textStyle="bodyMediumStrong" variant="secondary" as="div">
                <AvailabilityBlockTimeUnitCount serviceId={serviceId} endUtc={timeUnitEndUtc} startUtc={startUtc} />
                {timeUnitPeriod !== TimeUnitPeriod.Hour && (
                    <>
                        {SpacedComma}
                        <LocalizedDayNameFromUtc utcString={startUtc} />
                        {SpacedDash}
                        <LocalizedDayNameFromUtc utcString={timeUnitEndUtc} />
                    </>
                )}
            </Typography>
            <Body>
                <span data-test-start>
                    <LocalizedUtcTimeUnit timeUnit={timeUnitPeriod} utcString={startUtc} />
                </span>
                {SpacedDash}
                <span data-test-end>
                    <LocalizedUtcTimeUnit timeUnit={timeUnitPeriod} utcString={timeUnitEndUtc} />
                </span>
            </Body>
        </div>
    );
};
